import { useCallback, useEffect, useRef, useState } from 'react';
import { pmaImportAPI } from '../services/api';
import {
  DEFAULT_PMA_POLICY,
  policyToQuery,
  pmaWorkbookFilename,
  validatePmaIdentityPolicy,
} from '../domain/pma/pmaIdentityPolicy';
import { readPmaApiError, readPmaCreateFailurePayload } from '../domain/pma/pmaApiError';
import {
  PMA_CREATE_PROGRESS_DONE,
  PMA_DELETE_PROGRESS_DONE,
  createUserProgress,
  emptyLabOpAggregate,
  labOpAggregateResult,
  labOpRequestTimeoutMs,
  labOpUserLabels,
  mergeLabOpResponse,
} from '../domain/pma/pmaCreateProgress';
import {
  PMA_PROVISION_REPORT_FILENAME,
  buildProvisionReportCsv,
} from '../domain/pma/pmaProvisionReport';

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function downloadBase64Report(report) {
  if (!report?.base64) return false;
  const binary = atob(report.base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], {
    type: report.contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  downloadBlob(blob, report.filename || 'lab_linux_vnc_credentials.xlsx');
  return true;
}

function downloadCombinedProvisionReport(users = []) {
  if (!users.length) return false;
  const csv = buildProvisionReportCsv(users);
  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), PMA_PROVISION_REPORT_FILENAME);
  return true;
}

export default function usePmaStudentImport() {
  const [file, setFile] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [policy, setPolicy] = useState(DEFAULT_PMA_POLICY);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sendingToPma, setSendingToPma] = useState(false);
  const [deletingFromLab, setDeletingFromLab] = useState(false);
  const [accessBusy, setAccessBusy] = useState('');
  const [pmaSendResult, setPmaSendResult] = useState(null);
  const [pmaV2Configured, setPmaV2Configured] = useState(null);
  const [labCreateMeta, setLabCreateMeta] = useState(null);
  const [labDeleteMeta, setLabDeleteMeta] = useState(null);
  const [labAccessMeta, setLabAccessMeta] = useState(null);
  const [createProgress, setCreateProgress] = useState({
    active: false,
    done: false,
    percent: 0,
    label: '',
    currentUser: '',
    index: 0,
    total: 0,
    completedNames: [],
  });
  const createStartedAtRef = useRef(0);
  const createInFlightRef = useRef(false);
  const currentOpRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    pmaImportAPI
      .getTemplateMeta()
      .then((res) => {
        if (!cancelled) {
          setPmaV2Configured(Boolean(res.data?.pmaV2?.configured));
          setLabCreateMeta(res.data?.labCreate || null);
          setLabDeleteMeta(res.data?.labDelete || null);
          setLabAccessMeta(res.data?.labAccess || null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPmaV2Configured(false);
          setLabCreateMeta(null);
          setLabDeleteMeta(null);
          setLabAccessMeta(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const generateUserGen = useCallback(async (studentIds = []) => {
    setError('');
    setPmaSendResult(null);
    const check = validatePmaIdentityPolicy(policy);
    if (!check.valid) {
      setError(check.errors.join('\n'));
      setPreview(null);
      return;
    }
    if (!studentIds.length) {
      setError('Select at least one student before generating UserGen.xlsx');
      return;
    }
    setPreviewing(true);
    try {
      const res = await pmaImportAPI.generateLabUsers(studentIds, policyToQuery(policy));
      setPreview(res.data);
      setPmaSendResult(res.data.generate || res.data);
      if (res.data.issues?.length) {
        setError(
          res.data.issues
            .map((item) => `Row ${item.rowNumber}${item.email ? ` (${item.email})` : ''}: ${item.error}`)
            .join('\n')
        );
      }
    } catch (err) {
      setError(await readPmaApiError(err, 'Could not generate UserGen.xlsx'));
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  }, [policy]);

  const downloadUserGen = useCallback(async (studentIds = []) => {
    setError('');
    const check = validatePmaIdentityPolicy(policy);
    if (!check.valid) {
      setError(check.errors.join('\n'));
      return;
    }
    if (!studentIds.length) {
      setError('Select at least one student before generating UserGen.xlsx');
      return;
    }
    setDownloading(true);
    try {
      const res = await pmaImportAPI.downloadUserGen(studentIds, policyToQuery(policy));
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      downloadBlob(blob, 'UserGen.xlsx');
      const previewRes = await pmaImportAPI.previewGenerate(studentIds, policyToQuery(policy));
      setPreview(previewRes.data);
    } catch (err) {
      setError(await readPmaApiError(err, 'Could not download UserGen.xlsx'));
    } finally {
      setDownloading(false);
    }
  }, [policy]);

  const previewTemplate = useCallback(async (studentIds = []) => {
    setError('');
    const check = validatePmaIdentityPolicy(policy);
    if (!check.valid) {
      setError(check.errors.join('\n'));
      setPreview(null);
      return;
    }
    setPreviewing(true);
    try {
      const res = await pmaImportAPI.previewTemplate(studentIds, policyToQuery(policy));
      setPreview(res.data);
    } catch (err) {
      setError(await readPmaApiError(err, 'Could not preview the Excel'));
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  }, [policy]);

  const downloadTemplate = useCallback(async (studentIds = []) => {
    setError('');
    const check = validatePmaIdentityPolicy(policy);
    if (!check.valid) {
      setError(check.errors.join('\n'));
      return;
    }
    setDownloading(true);
    try {
      const res = await pmaImportAPI.downloadTemplate(studentIds, policyToQuery(policy));
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      downloadBlob(blob, pmaWorkbookFilename(policy));
      try {
        const previewRes = await pmaImportAPI.previewTemplate(studentIds, policyToQuery(policy));
        setPreview(previewRes.data);
      } catch {
        /* download already succeeded */
      }
    } catch (err) {
      setError(await readPmaApiError(err, 'Could not download the Excel template'));
    } finally {
      setDownloading(false);
    }
  }, [policy]);

  useEffect(() => {
    if (!sendingToPma && !deletingFromLab && !accessBusy) return undefined;
    const tick = () => {
      if (!createInFlightRef.current || !currentOpRef.current) return;
      const cur = currentOpRef.current;
      const next = createUserProgress({
        ...cur,
        elapsedMs: Date.now() - createStartedAtRef.current,
      });
      setCreateProgress((prev) => ({
        ...prev,
        active: true,
        done: false,
        percent: next.percent,
        label: next.label,
        currentUser: next.currentUser,
        index: next.index,
        total: next.total,
      }));
    };
    tick();
    const id = window.setInterval(tick, 400);
    return () => window.clearInterval(id);
  }, [sendingToPma, deletingFromLab, accessBusy]);

  const beginUserOp = (partial) => {
    currentOpRef.current = partial;
    createStartedAtRef.current = Date.now();
    const next = createUserProgress({ ...partial, elapsedMs: 0 });
    setCreateProgress((prev) => ({
      ...prev,
      active: true,
      done: false,
      percent: next.percent,
      label: next.label,
      currentUser: next.currentUser,
      index: next.index,
      total: next.total,
    }));
  };

  const formatLabIssues = (issues = []) =>
    issues
      .map((item) => {
        const step = item.step ? ` [${item.step}]` : '';
        return `Row ${item.rowNumber}${item.email ? ` (${item.email})` : ''}${step}: ${item.error}`;
      })
      .join('\n');

  const sendToPma = useCallback(async (studentIds = [], selectedStudents = []) => {
    setError('');
    setPmaSendResult(null);
    createInFlightRef.current = true;
    const check = validatePmaIdentityPolicy(policy);
    if (!check.valid) {
      createInFlightRef.current = false;
      setError(check.errors.join('\n'));
      setCreateProgress({ active: false, done: false, percent: 0, label: '', currentUser: '', index: 0, total: 0, completedNames: [] });
      return;
    }
    if (!studentIds.length) {
      createInFlightRef.current = false;
      setError('Select at least one student before sending to PMA');
      setCreateProgress({ active: false, done: false, percent: 0, label: '', currentUser: '', index: 0, total: 0, completedNames: [] });
      return;
    }
    const names = labOpUserLabels(studentIds, selectedStudents);
    const host = policy.vncFqdn || policy.puttyFqdn || '';
    const query = policyToQuery(policy);
    const timeout = labOpRequestTimeoutMs(1);
    setCreateProgress({
      active: true,
      done: false,
      percent: 4,
      label: `Creating ${names[0]} (1 of ${studentIds.length})…`,
      currentUser: names[0],
      index: 0,
      total: studentIds.length,
      completedNames: [],
    });
    setSendingToPma(true);
    let aggregate = emptyLabOpAggregate();
    const completedNames = [];
    const errorParts = [];
    try {
      for (let i = 0; i < studentIds.length; i += 1) {
        if (!createInFlightRef.current) break;
        const userName = names[i];
        beginUserOp({
          index: i,
          total: studentIds.length,
          userName,
          host,
          completed: i,
          verb: 'Creating',
        });
        try {
          const res = await pmaImportAPI.sendToPma([studentIds[i]], query, { timeout });
          aggregate = mergeLabOpResponse(aggregate, res.data);
          if (res.data.issues?.length) errorParts.push(formatLabIssues(res.data.issues));
        } catch (err) {
          const failure = readPmaCreateFailurePayload(err);
          if (failure) {
            aggregate = mergeLabOpResponse(aggregate, failure);
          } else {
            aggregate = mergeLabOpResponse(aggregate, {
              sms: { successCount: 0, errorCount: 1, totalRows: 1, users: [{ userId: userName }] },
              issues: [{ rowNumber: i + 1, userId: userName, error: err.message || 'Lab account create failed' }],
            });
          }
          errorParts.push(await readPmaApiError(err, `Lab account create failed for ${userName}`));
        }
        completedNames.push(userName);
        setCreateProgress((prev) => ({ ...prev, completedNames: [...completedNames] }));
      }
      const result = labOpAggregateResult(aggregate, { verb: 'Created' });
      setPmaSendResult(result);
      if (!downloadCombinedProvisionReport(result.sms.users)) {
        downloadBase64Report(result.report);
      }
      setCreateProgress({
        active: false,
        done: true,
        percent: PMA_CREATE_PROGRESS_DONE.at,
        label: result.sms.errorCount
          ? 'Credentials Excel ready (with errors)'
          : PMA_CREATE_PROGRESS_DONE.label,
        currentUser: '',
        index: studentIds.length,
        total: studentIds.length,
        completedNames,
      });
      if (errorParts.length) setError(errorParts.join('\n\n'));
    } finally {
      createInFlightRef.current = false;
      currentOpRef.current = null;
      setSendingToPma(false);
    }
  }, [policy]);

  const deleteFromLab = useCallback(async (studentIds = [], selectedStudents = []) => {
    setError('');
    setPmaSendResult(null);
    createInFlightRef.current = true;
    const check = validatePmaIdentityPolicy(policy);
    if (!check.valid) {
      createInFlightRef.current = false;
      setError(check.errors.join('\n'));
      setCreateProgress({ active: false, done: false, percent: 0, label: '', currentUser: '', index: 0, total: 0, completedNames: [] });
      return;
    }
    if (!studentIds.length) {
      createInFlightRef.current = false;
      setError('Select at least one student before deleting lab accounts');
      setCreateProgress({ active: false, done: false, percent: 0, label: '', currentUser: '', index: 0, total: 0, completedNames: [] });
      return;
    }
    const confirmed = window.confirm(
      `Remove ${studentIds.length} local lab Linux account(s) and their VNC service(s) on the practice host?\n\nThe home directory stays. A central login with the same name is not deleted. The student and lab record stay in SMS.`
    );
    if (!confirmed) {
      createInFlightRef.current = false;
      setCreateProgress({ active: false, done: false, percent: 0, label: '', currentUser: '', index: 0, total: 0, completedNames: [] });
      return;
    }
    const names = labOpUserLabels(studentIds, selectedStudents);
    const host = policy.vncFqdn || policy.puttyFqdn || '';
    const query = policyToQuery(policy);
    const timeout = labOpRequestTimeoutMs(1);
    setCreateProgress({
      active: true,
      done: false,
      percent: 4,
      label: `Deleting ${names[0]} (1 of ${studentIds.length})…`,
      currentUser: names[0],
      index: 0,
      total: studentIds.length,
      completedNames: [],
    });
    setDeletingFromLab(true);
    let aggregate = emptyLabOpAggregate();
    const completedNames = [];
    const errorParts = [];
    try {
      for (let i = 0; i < studentIds.length; i += 1) {
        if (!createInFlightRef.current) break;
        const userName = names[i];
        beginUserOp({
          index: i,
          total: studentIds.length,
          userName,
          host,
          completed: i,
          verb: 'Deleting',
        });
        try {
          const res = await pmaImportAPI.deleteFromLab([studentIds[i]], query, { timeout });
          aggregate = mergeLabOpResponse(aggregate, res.data);
          if (res.data.issues?.length) errorParts.push(formatLabIssues(res.data.issues));
        } catch (err) {
          const failure = readPmaCreateFailurePayload(err);
          if (failure) {
            aggregate = mergeLabOpResponse(aggregate, failure);
          } else {
            aggregate = mergeLabOpResponse(aggregate, {
              sms: { successCount: 0, errorCount: 1, totalRows: 1, users: [{ userId: userName }] },
              issues: [{ rowNumber: i + 1, userId: userName, error: err.message || 'Lab account delete failed' }],
            });
          }
          errorParts.push(await readPmaApiError(err, `Lab account delete failed for ${userName}`));
        }
        completedNames.push(userName);
        setCreateProgress((prev) => ({ ...prev, completedNames: [...completedNames] }));
      }
      const result = labOpAggregateResult(aggregate, { verb: 'Deleted' });
      setPmaSendResult(result);
      setCreateProgress({
        active: false,
        done: true,
        percent: PMA_DELETE_PROGRESS_DONE.at,
        label: result.sms.errorCount ? 'Delete finished with errors' : PMA_DELETE_PROGRESS_DONE.label,
        currentUser: '',
        index: studentIds.length,
        total: studentIds.length,
        completedNames,
      });
      if (errorParts.length) setError(errorParts.join('\n\n'));
    } finally {
      createInFlightRef.current = false;
      currentOpRef.current = null;
      setDeletingFromLab(false);
    }
  }, [policy]);

  const setLabAccess = useCallback(async (action, studentIds = [], selectedStudents = []) => {
    const enabling = action === 'enable';
    const verb = enabling ? 'Enable' : 'Disable';
    const verbing = enabling ? 'Enabling' : 'Disabling';
    setError('');
    setPmaSendResult(null);
    createInFlightRef.current = true;
    const check = validatePmaIdentityPolicy(policy);
    const resetIdle = () => {
      createInFlightRef.current = false;
      setCreateProgress({ active: false, done: false, percent: 0, label: '', currentUser: '', index: 0, total: 0, completedNames: [] });
    };
    if (!check.valid) {
      resetIdle();
      setError(check.errors.join('\n'));
      return;
    }
    if (!studentIds.length) {
      resetIdle();
      setError(`Select at least one student before you ${verb.toLowerCase()} lab accounts`);
      return;
    }
    const confirmed = window.confirm(
      enabling
        ? `Enable ${studentIds.length} lab Linux account(s)?\n\nThis unlocks the login and starts VNC again. The home directory is unchanged. Expiry is not extended.`
        : `Disable ${studentIds.length} lab Linux account(s)?\n\nThis locks the login and stops VNC. The account and home stay, so Enable can turn them back on. This is not Delete.`
    );
    if (!confirmed) {
      resetIdle();
      return;
    }
    const names = labOpUserLabels(studentIds, selectedStudents);
    const host = policy.vncFqdn || policy.puttyFqdn || '';
    const query = policyToQuery(policy);
    const timeout = labOpRequestTimeoutMs(1);
    setAccessBusy(action);
    setCreateProgress({
      active: true,
      done: false,
      percent: 4,
      label: `${verbing} ${names[0]} (1 of ${studentIds.length})…`,
      currentUser: names[0],
      index: 0,
      total: studentIds.length,
      completedNames: [],
    });
    let aggregate = emptyLabOpAggregate();
    const completedNames = [];
    const errorParts = [];
    try {
      for (let i = 0; i < studentIds.length; i += 1) {
        if (!createInFlightRef.current) break;
        const userName = names[i];
        beginUserOp({
          index: i,
          total: studentIds.length,
          userName,
          host,
          completed: i,
          verb: verbing,
        });
        try {
          const res = await pmaImportAPI.setLabAccess(action, [studentIds[i]], query, { timeout });
          aggregate = mergeLabOpResponse(aggregate, res.data);
          if (res.data.issues?.length) errorParts.push(formatLabIssues(res.data.issues));
        } catch (err) {
          const failure = readPmaCreateFailurePayload(err);
          if (failure) {
            aggregate = mergeLabOpResponse(aggregate, failure);
          } else {
            aggregate = mergeLabOpResponse(aggregate, {
              sms: { successCount: 0, errorCount: 1, totalRows: 1, users: [{ userId: userName }] },
              issues: [{ rowNumber: i + 1, userId: userName, error: err.message || `Lab account ${verb.toLowerCase()} failed` }],
            });
          }
          errorParts.push(await readPmaApiError(err, `Lab account ${verb.toLowerCase()} failed for ${userName}`));
        }
        completedNames.push(userName);
        setCreateProgress((prev) => ({ ...prev, completedNames: [...completedNames] }));
      }
      const result = labOpAggregateResult(aggregate, { verb });
      setPmaSendResult(result);
      setCreateProgress({
        active: false,
        done: true,
        percent: 100,
        label: result.sms.errorCount ? `${verb} finished with errors` : `Lab ${verb.toLowerCase()} finished`,
        currentUser: '',
        index: studentIds.length,
        total: studentIds.length,
        completedNames,
      });
      if (errorParts.length) setError(errorParts.join('\n\n'));
    } finally {
      createInFlightRef.current = false;
      currentOpRef.current = null;
      setAccessBusy('');
    }
  }, [policy]);

  const chooseFile = useCallback((selected) => {
    setResult(null);
    setError('');
    if (!selected) {
      setFile(null);
      return;
    }
    const name = selected.name.toLowerCase();
    if (!name.endsWith('.xlsx')) {
      setError('Please choose an Excel file (.xlsx)');
      setFile(null);
      return;
    }
    if (selected.size > 5 * 1024 * 1024) {
      setError('File is larger than 5MB');
      setFile(null);
      return;
    }
    setFile(selected);
  }, []);

  const uploadFile = useCallback(async () => {
    if (!file) {
      setError('Choose an Excel file first');
      return;
    }
    setError('');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await pmaImportAPI.upload(formData, policyToQuery(policy));
      setResult(res.data);
      setFile(null);
    } catch (err) {
      setError(await readPmaApiError(err, 'Upload failed'));
    } finally {
      setUploading(false);
    }
  }, [file, policy]);

  return {
    file,
    downloading,
    uploading,
    error,
    result,
    preview,
    previewing,
    generateUserGen,
    downloadUserGen,
    previewTemplate,
    downloadTemplate,
    sendToPma,
    sendingToPma,
    deleteFromLab,
    deletingFromLab,
    setLabAccess,
    accessBusy,
    createProgress,
    pmaSendResult,
    pmaV2Configured,
    labCreateMeta,
    labDeleteMeta,
    labAccessMeta,
    chooseFile,
    uploadFile,
    setError,
    setResult,
    policy,
    setPolicy,
  };
}
